import React, { useEffect, useMemo, useState } from "react";
import { Coins, IndianRupee, ArrowLeftRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { api, formatINR, formatNum } from "@/lib/api";
import { KARATS, PURITY_MAP } from "@/pages/admin/purchases/purchaseHelpers";

// Fine-gold factor for a purity choice: karat labels use the standard table, "995" (trade
// standard fine bar) and any custom per-mille value use fineness / 1000. Fine gold is always
// 24K-equivalent grams so a 995 bar settles at 0.995 g fine per gram weighed.
export const purityFactor = (purity, finePct) => {
  if (purity === "Other") return (Number(finePct) || 0) / 1000;
  if (PURITY_MAP[purity] !== undefined) return PURITY_MAP[purity];
  const n = Number(String(purity || "").replace(/[^\d.]/g, ""));
  return n > 24 ? Math.min(n, 1000) / 1000 : 1;
};

const CASH_MODES = [
  ["bank_transfer", "Bank Transfer"], ["upi", "UPI"], ["cash", "Cash"], ["cheque", "Cheque"], ["other", "Other"],
];

// Records a supplier payment for a purchase.
//   Cash (INR) tab  -> settles the RUPEE payable (bank/UPI/cash/cheque), or converts to settle
//                      the GOLD payable at a fixed rate (gold_to_cash).
//   Metal (Gold) tab -> weight + purity; debits OUR Fine Gold Stock and credits the supplier's
//                      Gold Ledger (gold_to_gold), or converts to settle the CASH payable at a
//                      fixed rate (cash_to_gold).
// The backend posts stock, supplier ledger and company ledger entries automatically.
export const RecordPurchasePaymentDialog = ({ open, onClose, detail, payForm, setPayForm, onSubmit }) => {
  const [tab, setTab] = useState("cash");
  const [goldStock, setGoldStock] = useState(null);

  useEffect(() => {
    if (!open) return;
    const goldOwed = Number(detail?.gold_outstanding_fine_g) || 0;
    const cashOwed = Number(detail?.outstanding_amount) || 0;
    const initial = goldOwed > 0 && cashOwed <= 0 ? "metal" : "cash";
    setTab(initial);
    setPayForm((f) => ({ ...f, mode: initial === "metal" ? "gold_to_gold" : "bank_transfer", gold_purity: initial === "metal" ? "995" : f.gold_purity || "22K" }));
    api.get("/purchase/material-stock/summary")
      .then(({ data }) => setGoldStock((data.balances || []).filter((b) => b.material_type === "gold").reduce((s, b) => s + (Number(b.qty) || 0), 0)))
      .catch(() => setGoldStock(null));
    // eslint-disable-next-line
  }, [open, detail?.id]);

  const switchTab = (t) => {
    setTab(t);
    setPayForm({ ...payForm, mode: t === "metal" ? "gold_to_gold" : "bank_transfer", amount: t === "metal" ? "" : payForm.amount, gold_weight_g: "", gold_purity: t === "metal" ? "995" : payForm.gold_purity, rate_per_gram_24k: "" });
  };

  const fineG = useMemo(() => {
    const w = Number(payForm.gold_weight_g) || 0;
    return Math.round(w * purityFactor(payForm.gold_purity, payForm.gold_purity_fine_pct) * 1000) / 1000;
  }, [payForm.gold_weight_g, payForm.gold_purity, payForm.gold_purity_fine_pct]);

  const isMetal = tab === "metal";
  const convert = isMetal ? payForm.mode === "cash_to_gold" : payForm.mode === "gold_to_cash";
  const cashMode = CASH_MODES.some(([m]) => m === payForm.mode) ? payForm.mode : "bank_transfer";
  const goldOwed = Number(detail?.gold_outstanding_fine_g) || 0;
  const cashOwed = Number(detail?.outstanding_amount) || 0;
  const overStock = isMetal && goldStock !== null && fineG > goldStock + 0.001;
  const rate = Number(payForm.rate_per_gram_24k) || 0;
  const convertedValue = convert ? (isMetal ? fineG * rate : (Number(payForm.amount) || 0) / (rate || 1)) : 0;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="purchase-payment-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Record Payment — {detail?.purchase_no}</DialogTitle>
          <DialogDescription>Settle {detail?.supplier_name} in cash or in metal. Stock and both ledgers update automatically.</DialogDescription>
        </DialogHeader>

        {detail && (
          <div className="rounded-lg border border-border bg-[#F4EBDD] p-3 text-xs grid grid-cols-2 gap-2" data-testid="purchase-payment-owed">
            <div><div className="uppercase text-muted-foreground">Gold owed</div><div className="font-mono text-sm">{formatNum(goldOwed, 3)} g fine</div></div>
            <div><div className="uppercase text-muted-foreground">Cash owed</div><div className="font-mono text-sm">{formatINR(cashOwed)}</div></div>
          </div>
        )}

        <Tabs value={tab} onValueChange={switchTab}>
          <TabsList className="grid grid-cols-2 w-full" data-testid="payment-kind-tabs">
            <TabsTrigger data-testid="payment-tab-cash" value="cash" className="gap-1.5"><IndianRupee className="h-3.5 w-3.5" /> Cash (INR)</TabsTrigger>
            <TabsTrigger data-testid="payment-tab-metal" value="metal" className="gap-1.5"><Coins className="h-3.5 w-3.5" /> Metal (Gold)</TabsTrigger>
          </TabsList>
        </Tabs>

        <div className="space-y-3">
          {!isMetal && (
            <>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Mode</Label>
                  <Select value={cashMode} onValueChange={(v) => setPayForm({ ...payForm, mode: convert ? "gold_to_cash" : v, cash_mode: v })}>
                    <SelectTrigger data-testid="purchase-payment-mode-select" className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>{CASH_MODES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label className="text-xs">Amount (₹)</Label><Input data-testid="purchase-payment-amount-input" type="number" min="0" value={payForm.amount} onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })} className="mt-1 font-mono" /></div>
              </div>
              <label className="flex items-start gap-2 text-xs cursor-pointer rounded-lg border border-border p-2.5 hover:bg-[#F7F0E6]">
                <input data-testid="payment-cash-convert-toggle" type="checkbox" className="mt-0.5 accent-[hsl(var(--primary))]" checked={convert}
                       onChange={(e) => setPayForm({ ...payForm, mode: e.target.checked ? "gold_to_cash" : (payForm.cash_mode || "bank_transfer") })} />
                <span><span className="font-medium flex items-center gap-1"><ArrowLeftRight className="h-3 w-3" /> Apply this cash against the GOLD owed</span><span className="text-muted-foreground">Converts at a fixed ₹/g (24K) rate — no metal moves; only the gold ledger is credited.</span></span>
              </label>
            </>
          )}

          {isMetal && (
            <>
              <div className="grid grid-cols-2 gap-2">
                <div><Label className="text-xs">Weight (g)</Label><Input data-testid="purchase-payment-gold-weight-input" type="number" min="0" step="0.001" value={payForm.gold_weight_g} onChange={(e) => setPayForm({ ...payForm, gold_weight_g: e.target.value, amount: "" })} className="mt-1 font-mono" placeholder="as weighed" /></div>
                <div>
                  <Label className="text-xs">Purity</Label>
                  <Select value={payForm.gold_purity || "995"} onValueChange={(v) => setPayForm({ ...payForm, gold_purity: v })}>
                    <SelectTrigger data-testid="purchase-payment-purity-select" className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="995">995 Fine (bar / bullion)</SelectItem>
                      <SelectItem value="999">999 Fine</SelectItem>
                      {KARATS.map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}
                      <SelectItem value="Other">Other (‰)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {payForm.gold_purity === "Other" && (
                <div><Label className="text-xs">Fineness (‰)</Label><Input data-testid="purchase-payment-fineness-input" type="number" min="1" max="1000" value={payForm.gold_purity_fine_pct} onChange={(e) => setPayForm({ ...payForm, gold_purity_fine_pct: e.target.value })} className="mt-1 font-mono" placeholder="e.g. 916" /></div>
              )}
              <div className={`rounded-lg border p-2.5 text-xs flex items-center justify-between ${overStock ? "border-[#E7B9B9] bg-[#FBE7E7]" : "border-border bg-[#F7F0E6]"}`} data-testid="payment-fine-gold-preview">
                <span>Fine gold (24K eq.) <b className="font-mono">{formatNum(fineG, 3)} g</b></span>
                <span className={overStock ? "text-[#7A1F1F]" : "text-muted-foreground"}>Stock: <b className="font-mono">{goldStock === null ? "…" : `${formatNum(goldStock)} g`}</b>{overStock ? " — not enough" : ""}</span>
              </div>
              <label className="flex items-start gap-2 text-xs cursor-pointer rounded-lg border border-border p-2.5 hover:bg-[#F7F0E6]">
                <input data-testid="payment-metal-convert-toggle" type="checkbox" className="mt-0.5 accent-[hsl(var(--primary))]" checked={convert}
                       onChange={(e) => setPayForm({ ...payForm, mode: e.target.checked ? "cash_to_gold" : "gold_to_gold" })} />
                <span><span className="font-medium flex items-center gap-1"><ArrowLeftRight className="h-3 w-3" /> Apply this metal against the CASH owed</span><span className="text-muted-foreground">Converts at a fixed ₹/g (24K) rate; the fine gold still leaves our stock.</span></span>
              </label>
              {!convert && <p className="text-[11px] text-muted-foreground">Debits {formatNum(fineG, 3)} g fine from our Fine Gold Stock and credits the supplier&apos;s Gold Ledger by the same amount.</p>}
            </>
          )}

          {convert && (
            <div className="grid grid-cols-2 gap-2 items-end">
              <div><Label className="text-xs">Fixed rate (₹ / g, 24K)</Label><Input data-testid="purchase-payment-rate-input" type="number" min="0" value={payForm.rate_per_gram_24k} onChange={(e) => setPayForm({ ...payForm, rate_per_gram_24k: e.target.value })} className="mt-1 font-mono" placeholder="e.g. 6800" /></div>
              <div className="text-xs text-muted-foreground pb-2 font-mono">{rate > 0 ? (isMetal ? `= ${formatINR(convertedValue)} of cash owed` : `= ${formatNum(convertedValue)} g fine of gold owed`) : "Enter the settlement rate"}</div>
            </div>
          )}

          <div><Label className="text-xs">Reference / UTR / bar no.</Label><Input data-testid="purchase-payment-reference-input" value={payForm.reference} onChange={(e) => setPayForm({ ...payForm, reference: e.target.value })} className="mt-1" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="purchase-payment-submit" onClick={onSubmit} disabled={overStock || (isMetal ? !(Number(payForm.gold_weight_g) > 0) : !(Number(payForm.amount) > 0)) || (convert && !(rate > 0))}>
            {isMetal ? "Record Metal Payment" : "Record Cash Payment"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
